/**
 * 时间锚点定义处 —— **全仓唯一允许出现时区字面量的地方**（I-7、ADR-0051）。
 *
 * ## I-7 的两半
 *
 * | | 时刻 | 业务日 |
 * |---|---|---|
 * | 是什么 | 一件事发生在**哪一瞬** | 这一瞬算**哪一天的账** |
 * | 有没有时区 | **绝对的，无时区可言** | **由锚点决定** |
 * | 存什么 | UTC。**不是配置项，没有选择** | **不存**，按锚点推导 |
 * | 谁能改 | **没有人** | 客户，改 `Organization.timezone` |
 *
 * ## 这个文件为什么存在
 *
 * 时区字面量（`'Asia/Shanghai'` 这类）在别处出现一次，就多一处**与锚点竞争的定义**。
 * 2026-08-18 全仓查出九处，其中最隐蔽的一处是
 * `organization.ts` 里 `timezone` 的 `.default('Asia/Shanghai')`——
 * **它长得像配置，实际是代码替客户做了决定**。
 *
 * 故：字面量集中在本文件，别处一律引用；lint 与测试两道守卫盯着这条
 * （见 `eslint.config.js` 与 `services/api/test/i7-time-anchor.test.ts`）。
 *
 * ## 业务日不在这里算
 *
 * 本文件**不导出任何算业务日的函数**。业务日由数据库函数 `app.biz_date(tenant_id, at)`
 * 唯一决定——因为视图与库级闸门调不到应用层，而业务日在两处必须是同一个定义
 * （两份实现就是坑清单第 26 条的形状）。
 *
 * 这里只有「有哪些时区可选」与「锚点是谁」这两件事。
 */

/**
 * 界面下拉的候选时区。
 *
 * **不是全量 IANA 表**——给客户 600 个选项等于没给。这里放中国客户实际会用到的，
 * 需要别的就往里加一行，**加在这里，不要在界面上就地写一个**。
 */
export const TIMEZONE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai · 中国标准时间 UTC+8' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong · 香港时间 UTC+8' },
  { value: 'Asia/Taipei', label: 'Asia/Taipei · 台北时间 UTC+8' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo · 日本标准时间 UTC+9' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore · 新加坡时间 UTC+8' },
  { value: 'Europe/London', label: 'Europe/London · 英国时间 UTC+0/+1' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles · 美西时间 UTC-8/-7' },
  { value: 'UTC', label: 'UTC · 协调世界时' },
] as const;

/**
 * 界面下拉的**初值**，仅作光标落点。
 *
 * **它不是默认值。** 契约层此前有 `.default('Asia/Shanghai')`，那才是默认值——
 * 已按 I-7 去掉：不填时区时不该由代码替客户决定归日口径，**这件事本来就该是人的决定**。
 *
 * 两者的区别在于**谁在负责**：初值是「光标停在这里，你可以改」，
 * 默认值是「你没说，我替你定了」。后者会让一个没人做过的决定看起来像做过了。
 */
export const TIMEZONE_PICKER_INITIAL = 'Asia/Shanghai';

/** 锚点所在：企业的 `timezone` 列。业务日归属由它唯一决定（I-7）。 */
export const TIME_ANCHOR = {
  table: 'Organization',
  column: 'timezone',
  /** 业务日的唯一计算入口。应用侧要业务日一律调它，不要自己 `at time zone`。 */
  bizDateFunction: 'app.biz_date',
} as const;
